import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { CostSettingCost, CostSettingProfile } from 'lumen/models';
import type { CostEditModel } from 'lumen/routes/market/settings/cost/edit';
import type { BookingWindow } from 'lumen/utils/net-revenue/cost/booking-window';
import {
  buildDefaultBookingWindow,
  cloneBookingWindow,
} from 'lumen/utils/net-revenue/cost/booking-window';
import {
  type CostProfileFormState,
  profileToFormState,
} from 'lumen/utils/net-revenue/cost/profile-to-form';
import type {
  BookingWindowRow,
  CommissionMode,
  CommissionUnit,
  CostValuesView,
} from 'lumen/utils/net-revenue/cost/types';
import type { ControllerWithUnsavedChanges } from 'lumen/utils/unsaved-changes-transition-handlers';

function arraysEqual(a: (number | null)[], b: (number | null)[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export default class MarketSettingsCostEditController
  extends Controller
  implements ControllerWithUnsavedChanges
{
  declare model: CostEditModel;

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked bookingWindows: BookingWindow[] = [buildDefaultBookingWindow()];
  @tracked currencyValue: number | null = null;
  @tracked description = '';
  @tracked hasAttemptedSave = false;
  @tracked mode: CommissionMode = 'simple';
  @tracked name = '';
  @tracked percentageValue: number | null = null;
  @tracked profile: CostSettingProfile | undefined;
  @tracked unit: CommissionUnit = 'percentage';

  // Unlike `new`, this baseline is the loaded profile, not blank defaults.
  initialState: CostProfileFormState | null = null;

  loadTask = task({ drop: true }, async (): Promise<void> => {
    try {
      await this.model.profiles.last;
    } catch {
      // Already alerted by the parent route; fall through to the redirect below.
    }

    const profile = this.model.profiles.last?.value?.find(
      (candidate) => candidate.id === this.model.profileId,
    );

    if (!profile) {
      // Stale or mistyped id, so there is nothing to edit.
      this.router.transitionTo('market.settings.cost.index');

      return;
    }

    this.profile = profile;
    this.initializeFromProfile(profile);
  });

  saveTask = task({ drop: true }, async (): Promise<void> => {
    this.hasAttemptedSave = true;

    const { profile } = this;
    if (!profile || !this.isValid) {
      return;
    }

    const costValues: CostValuesView =
      this.mode === 'advanced'
        ? { bookingWindows: this.bookingWindowsForSave, value: null }
        : { bookingWindows: [], value: this.commissionValue };

    // Mutate instead of replace, so `rollbackAttributes` is the whole undo.
    const cost =
      profile.costs[0] ??
      this.store.createRecord<CostSettingCost>('cost-setting/cost', {
        profile,
      });

    cost.commissionType = this.mode;
    cost.costValues = costValues;
    cost.valueType = this.unit;

    profile.name = this.name.trim();
    profile.description = this.description.trim() || null;

    try {
      await profile.save();
    } catch (error) {
      cost.rollbackAttributes();
      profile.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });

      return;
    }

    // Re-baseline to the saved values, or `hasUnsavedChanges` would keep
    // seeing the old state as dirty and block the next navigation.
    this.initialState = {
      name: this.name,
      description: this.description,
      mode: this.mode,
      unit: this.unit,
      currencyValue: this.currencyValue,
      percentageValue: this.percentageValue,
      bookingWindows: this.bookingWindows.map(cloneBookingWindow),
    };

    try {
      await this.model.profiles.perform();
    } catch {
      // Already saved, and the cost route alerts on a failed refresh.
    }

    this.toast.success(this.intl.t('settings.cost.updated'), {
      showIcon: true,
    });
  });

  get bookingWindowRows(): BookingWindowRow[] {
    return this.bookingWindows.map((bookingWindow) => ({
      id: bookingWindow.id,
      label: this.intl.t('settings.cost.advanced.all_default'),
      values: this.activeValues(bookingWindow),
    }));
  }

  get bookingWindowsForSave(): CostValuesView['bookingWindows'] {
    return this.bookingWindows.map((bookingWindow) => ({
      leadtimeEnd: bookingWindow.leadtimeEnd,
      leadtimeStart: bookingWindow.leadtimeStart,
      values: this.activeValues(bookingWindow),
    }));
  }

  get commissionValue(): number | null {
    return this.unit === 'currency' ? this.currencyValue : this.percentageValue;
  }

  get costError(): string | undefined {
    if (!this.hasAttemptedSave) {
      return undefined;
    }

    if (this.mode === 'advanced') {
      return this.isAdvancedMatrixComplete
        ? undefined
        : this.intl.t('settings.cost.error.matrix_required');
    }

    return this.commissionValue === null
      ? this.intl.t('settings.cost.error.cost_required')
      : undefined;
  }

  // Excludes its own name, so re-saving an unchanged name is allowed.
  get existingNames(): Set<string> {
    const profiles = this.model.profiles.last?.value ?? [];

    return new Set(
      profiles
        .filter((candidate) => candidate.id !== this.model.profileId)
        .map((profile) => (profile.name ?? '').trim().toLowerCase()),
    );
  }

  get hasUnsavedChanges(): boolean {
    const initial = this.initialState;
    if (!initial) {
      return false;
    }

    return (
      this.name.trim() !== initial.name.trim() ||
      this.description.trim() !== initial.description.trim() ||
      this.mode !== initial.mode ||
      this.unit !== initial.unit ||
      this.currencyValue !== initial.currencyValue ||
      this.percentageValue !== initial.percentageValue ||
      this.bookingWindows.some((bookingWindow, index) => {
        const initialBookingWindow = initial.bookingWindows[index];
        if (!initialBookingWindow) {
          return true;
        }

        return (
          !arraysEqual(
            bookingWindow.currencyValues,
            initialBookingWindow.currencyValues,
          ) ||
          !arraysEqual(
            bookingWindow.percentageValues,
            initialBookingWindow.percentageValues,
          )
        );
      })
    );
  }

  // Only the active unit's cells count — the other unit can stay empty.
  get isAdvancedMatrixComplete(): boolean {
    return this.bookingWindows.every((bookingWindow) =>
      this.activeValues(bookingWindow).every((value) => value !== null),
    );
  }

  get isLoading(): boolean {
    return this.loadTask.isRunning;
  }

  get isNameUnique(): boolean {
    return !this.existingNames.has(this.name.trim().toLowerCase());
  }

  get isSaveDisabled(): boolean {
    return (
      !this.profile ||
      this.saveTask.isRunning ||
      this.nameError !== undefined ||
      this.costError !== undefined
    );
  }

  get isValid(): boolean {
    if (this.name.trim().length === 0 || !this.isNameUnique) {
      return false;
    }

    return this.mode === 'advanced'
      ? this.isAdvancedMatrixComplete
      : this.commissionValue !== null;
  }

  get nameError(): string | undefined {
    if (!this.hasAttemptedSave) {
      return undefined;
    }

    if (this.name.trim().length === 0) {
      return this.intl.t('settings.cost.name.error.required');
    }

    if (!this.isNameUnique) {
      return this.intl.t('settings.cost.name.error.not_unique');
    }

    return undefined;
  }

  activeValues(bookingWindow: BookingWindow): (number | null)[] {
    return this.unit === 'currency'
      ? bookingWindow.currencyValues
      : bookingWindow.percentageValues;
  }

  applyState(state: CostProfileFormState): void {
    this.name = state.name;
    this.description = state.description;
    this.mode = state.mode;
    this.unit = state.unit;
    this.currencyValue = state.currencyValue;
    this.percentageValue = state.percentageValue;
    // Clone, or editing a cell would also mutate the `initialState` baseline
    // this was applied from.
    this.bookingWindows = state.bookingWindows.map(cloneBookingWindow);
  }

  initializeFromProfile(profile: CostSettingProfile): void {
    const state = profileToFormState(profile);
    this.initialState = state;
    this.applyState(state);
  }

  @action discardChanges(): void {
    if (!this.initialState) {
      return;
    }

    this.hasAttemptedSave = false;
    this.applyState(this.initialState);
  }

  @action showDiscardChangesAlert(): Promise<boolean> {
    return this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t('general.unsaved_changes.description'),
      buttons: {
        cancel: { text: this.intl.t('general.keep_editing') },
        confirm: { text: this.intl.t('general.discard_changes') },
      },
    });
  }

  @action updateAdvancedCell(
    bookingWindowId: string,
    dayIndex: number,
    value: number | null,
  ): void {
    const bookingWindow = this.bookingWindows.find(
      (candidate) => candidate.id === bookingWindowId,
    );
    if (!bookingWindow) {
      return;
    }

    const values = this.activeValues(bookingWindow);
    if (values[dayIndex] === value) {
      return;
    }

    const next = [...values];
    next[dayIndex] = value;
    if (this.unit === 'currency') {
      bookingWindow.currencyValues = next;

      return;
    }

    bookingWindow.percentageValues = next;
  }

  @action updateCommissionValue(value: number | null): void {
    if (this.unit === 'currency') {
      this.currencyValue = value;

      return;
    }

    this.percentageValue = value;
  }

  @action updateDescription(event: Event): void {
    this.description = (event.target as HTMLInputElement).value;
  }

  @action updateMode(mode: CommissionMode): void {
    this.mode = mode;
  }

  @action updateName(event: Event): void {
    this.name = (event.target as HTMLInputElement).value;
  }

  @action updateUnit(unit: CommissionUnit): void {
    this.unit = unit;
  }
}
